import React, { useId } from 'react';
import { MODERATIONS_TEXTE } from '../../utils/moderation';
import { ModerationsHinweis } from './ModerationsHinweis';

/**
 * Die Bezahlschranke (`isPremium`) — EIN Baustein für Audio, Video, MindBook und Blog
 * (⭐️ 2026-10-02 nach 💎s Abnahme, BACKLOG `cms-bezahlschranke-gesperrt-wert-unlesbar`).
 *
 * Vorher baute jedes Formular das Feld selbst, in drei Bauformen (Größe, Beschriftung, Zeiger), und
 * sperrte es für die Redaktion mit `disabled` plus `disabled:opacity-50`. Zwei Folgen, gemessen auf dev:
 *   - Der WERT war kaum zu lesen — Häkchen gegen Grund ≈ 1,1:1. Ob ein Werk Premium ist, ist genau die
 *     Auskunft, die die Redaktion am gesperrten Feld noch braucht.
 *   - Die Tastatur übersprang das Feld; wer mit Tab durchs Formular geht, hörte weder Wert noch Grund.
 *
 * Deshalb `aria-disabled` statt `disabled`: das Feld bleibt Tab-Halt, die Vorlesehilfe sagt beim Fokus
 * „nicht verfügbar" UND die Begründung (`aria-describedby`), volle Deckkraft, Zeiger `not-allowed` an Feld
 * und Beschriftung. Die Sperre selbst trägt der Handler: eine Änderung wird verworfen, React stellt das
 * kontrollierte Feld auf `wert` zurück.
 *
 * ⚠️ Das ist KEIN Schutz — der Server weist eine Änderung durch die Redaktion mit 403 ab (#1367), siehe
 * `utils/moderation.ts`. Der Baustein ist Ehrlichkeit der Oberfläche.
 *
 * `children` steht zwischen Feld und Moderations-Hinweis (der Blog erklärt dort „an/aus"). Ist es gesetzt,
 * bekommt es eine eigene Kennung und zählt zur Beschreibung des Feldes.
 */
export const PremiumFeld: React.FC<{
  wert: boolean;
  onÄndern: (wert: boolean) => void;
  gesperrt: boolean;
  children?: React.ReactNode;
}> = ({ wert, onÄndern, gesperrt, children }) => {
  const id = useId();
  const zusatzId = `${id}-zusatz`;
  const hinweisId = `${id}-hinweis`;
  const beschreibung = [children ? zusatzId : null, gesperrt ? hinweisId : null].filter(Boolean).join(' ');
  const zeiger = gesperrt ? 'cursor-not-allowed' : 'cursor-pointer';

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={id}
          checked={wert}
          onChange={(e) => {
            if (!gesperrt) onÄndern(e.target.checked);
          }}
          aria-disabled={gesperrt || undefined}
          aria-describedby={beschreibung || undefined}
          className={`h-4 w-4 rounded border-secondary-300 ${zeiger}`}
        />
        <label htmlFor={id} className={`text-sm font-medium text-secondary-700 ${zeiger}`}>
          Premium-Inhalt
        </label>
      </div>
      {children && (
        <p id={zusatzId} className="mt-1 text-xs text-secondary-500">
          {children}
        </p>
      )}
      {gesperrt && <ModerationsHinweis id={hinweisId}>{MODERATIONS_TEXTE.premiumHinweis}</ModerationsHinweis>}
    </div>
  );
};
